import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiInboxArchiveLine } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type {
  CreateDownloadJobErrorResponse,
  CreateDownloadJobResponse,
  DownloadJobStatusResponse,
  MediaLibrarySearchFileResponse,
  FileDetailsResponse,
} from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'

const cropStrategy = {
  1: {
    cm: 'pad_resize',
  },
  2: {
    c: 'force',
  },
  3: {
    c: 'at_max',
  },
  4: {
    c: 'at_least',
  },
  5: {
    cm: 'maintain_ratio',
  },
  6: {
    cm: 'extract',
  },
  7: {
    cm: 'pad_extract',
  },
}

interface Props {
  isOpen: boolean
  onClose(): void
  fileDetails?: FileDetailsResponse
}

interface FormProps {
  addTransformations: boolean
  h: Number
  w: Number
  cropStrategy?: '1' | '2' | '3' | '4' | '5' | '6' | '7'
}

const DownloadModal = ({ isOpen, onClose, fileDetails }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isPublicLink, urlPrefix } = useContext(PublicLinksInfoContext)
  const [formError, setFormError] = useState<string>('')
  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  let [
    { selectedFiles, selectedFolders, selectedMediaCollections },
    dispatch,
  ] = useContext(MLContext)

  if (fileDetails) {
    selectedFiles = [fileDetails as unknown as MediaLibrarySearchFileResponse]
    selectedFolders = []
    selectedMediaCollections = []
  }
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            addTransformations: false,
            h: 640,
            w: 480,
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            const fileIds = selectedFiles.map(file => file.fileId)
            const folderIds = selectedFolders.map(folder => folder.folderId)
            const mediaCollectionIDs = selectedMediaCollections.map(
              mediaCollection => mediaCollection.mediaCollectionId,
            )

            let items: string[] = []
            const endpoint = `${urlPrefix}api/v1/download/`
            items = [...fileIds, ...folderIds, ...mediaCollectionIDs]

            let res = (await request.post(endpoint, {
              items,
              transformations: values.addTransformations
                ? [
                    {
                      ...(values.h && { h: values.h }),
                      ...(values.w && { w: values.w }),
                      ...(values.h &&
                        values.w &&
                        values.cropStrategy &&
                        cropStrategy[values.cropStrategy]),
                    },
                  ]
                : [],
            })) as CreateDownloadJobResponse | CreateDownloadJobErrorResponse

            if (request.error) {
              if ([400, 404].includes(response.status)) {
                setFormError((res as CreateDownloadJobErrorResponse).message)
              } else {
                setFormError(
                  t(
                    'error_in_downloading_items',
                    'Error in downloading items, please try again after sometime',
                  ),
                )
              }
            } else {
              if (selectedMediaCollections.length > 0) {
                window.trackEvent({
                  action: 'Submit',
                  category: 'Media collection',
                  label: 'Download as zip',
                  value: selectedMediaCollections.length,
                })
              }

              toast({
                title: t(
                  'download_success_title',
                  'Download request submitted successfully',
                ),
                description: t(
                  'download_success_description',
                  // eslint-disable-next-line max-len
                  'Your download is being processed and will begin shortly. You can also download the requested ZIP file from the Downloads section once it is ready.',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              setFormError('')
              onClose()


              dispatch([
                {
                  type: MediaLibraryActions.ADD_OPERATION,
                  payload: {
                    jobId: (res as CreateDownloadJobResponse).jobId,
                    status: (res as CreateDownloadJobResponse).status,
                    name: (res as CreateDownloadJobResponse).name,
                  },
                },
              ])
            }
          }}
          validationSchema={Yup.object().shape({
            addTransformations: Yup.boolean().required('Required'),
            h: Yup.number().typeError('Height must be a number'),
            w: Yup.number().typeError('Width must be a number'),
            cropStrategy: Yup.mixed().oneOf(
              ['1', '2', '3', '4', '5', '6', '7', ''],
              'Must be a valid crop strategy',
            ),
          })}
        >
          {({ values, handleChange, isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={RiInboxArchiveLine}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t(
                  'download_modal_description',
                  'Download assets as a ZIP archive',
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert minW="unset" status="error" mt="1em" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                {!isPublicLink ? (
                  <>
                    <Field name="addTransformations">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          marginTop="1em"
                          display="flex"
                          isInvalid={Boolean(
                            form.errors.addTransformations &&
                              form.touched.addTransformations,
                          )}
                          isRequired
                        >
                          <FormLabel htmlFor="addTransformations">
                            {t(
                              'apply_transformations',
                              'Apply transformations',
                            )}
                          </FormLabel>
                          <Switch
                            {...field}
                            id="addTransformations"
                            isInvalid={Boolean(
                              form.errors.addTransformations &&
                                form.touched.addTransformations,
                            )}
                            isChecked={values.addTransformations}
                            onChange={handleChange}
                          />
                          <FormErrorMessage>
                            {form.errors.addTransformations}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    {values.addTransformations && (
                      <Field name="h">
                        {({ field, form }: FieldProps<string, FormProps>) => (
                          <FormControl
                            marginTop="1em"
                            id="h"
                            isInvalid={Boolean(form.errors.h && form.touched.h)}
                          >
                            <FormLabel htmlFor="h">
                              {t('height', 'Height')}
                            </FormLabel>
                            <NumberInput
                              id="h"
                              {...field}
                              min={0}
                              onChange={val =>
                                void form.setFieldValue(field.name, val)
                              }
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                            <FormErrorMessage>{form.errors.h}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {values.addTransformations && (
                      <Field name="w">
                        {({ field, form }: FieldProps<string, FormProps>) => (
                          <FormControl
                            marginTop="1em"
                            id="w"
                            isInvalid={Boolean(form.errors.w && form.touched.w)}
                          >
                            <FormLabel htmlFor="w">
                              {t('width', 'Width')}
                            </FormLabel>
                            <NumberInput
                              id="w"
                              {...field}
                              min={0}
                              onChange={val =>
                                void form.setFieldValue(field.name, val)
                              }
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                            <FormErrorMessage>{form.errors.w}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    )}
                    {values.addTransformations && (
                      <Field name="cropStrategy">
                        {({ field, form }: FieldProps<string, FormProps>) => (
                          <FormControl
                            marginTop="1em"
                            isInvalid={Boolean(
                              form.errors.cropStrategy &&
                                form.touched.cropStrategy,
                            )}
                          >
                            <FormLabel>
                              {t('crop_strategy', 'Crop strategy')}
                            </FormLabel>
                            <Select
                              {...field}
                              isDisabled={Boolean(!(values.h && values.w))}
                            >
                              <option value="">{t('select', 'Select')}</option>
                              <option value="1">
                                {t(
                                  'pad_resize_crop',
                                  'Pad resize (cm-pad_resize)',
                                )}
                              </option>
                              <option value="2">
                                {t('forced_crop', 'Forced (c-force)')}
                              </option>
                              <option value="3">
                                {t('max_size_crop', 'Max-size (c-at_max)')}
                              </option>
                              <option value="4">
                                {t(
                                  'min_size_crop',
                                  'Min-size crop (c-at_least)',
                                )}
                              </option>
                              <option value="5">
                                {t(
                                  'maintain_ratio_crop',
                                  'Maintain ratio (c-maintain_ratio)',
                                )}
                              </option>
                              <option value="6">
                                {t('extract_crop', 'Extract (cm-extract)')}
                              </option>
                              <option value="7">
                                {t(
                                  'pad_extract_crop',
                                  'Pad extract (cm-pad_extract)',
                                )}
                              </option>
                            </Select>
                            <FormErrorMessage>
                              {form.errors.cropStrategy}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    )}
                  </>
                ) : (
                  <Text>
                    {t(
                      'download_zip_modal_description_public_link',
                      'Are you sure you want to download the asset(s) as a ZIP file?',
                    )}
                  </Text>
                )}
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submitForm()
                  }}
                >
                  {t('download', 'Download')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default DownloadModal
